import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close,
  Phone,
  Person,
  CalendarToday,
  Construction,
  MeetingRoom,
  Apartment,
  OpenInNew,
  Layers,
  List as ListIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Project, UnitStatus } from '../../types';
import { useProjectProperties } from '../../hooks/useApiCache';
import FloorPlanView from './FloorPlanView';

interface ProjectDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  project: Project | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} role="tabpanel">
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const unitStatusColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  [UnitStatus.AVAILABLE]: 'success',
  [UnitStatus.RESERVED]: 'warning',
  [UnitStatus.SOLD]: 'error',
  [UnitStatus.RENTED]: 'info',
};

// ─── Sub-component: Units table ───────────────────────────────────────────────
const ProjectUnitsTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { data: units, isLoading, error } = useProjectProperties(projectId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load units. Please try again.</Alert>;
  }

  if (!units || units.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Apartment sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No units linked to this project yet.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Create a property and link it to this project to see it here.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Floor</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Agent</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {units.map((unit: any) => (
            <TableRow
              key={unit.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'grey.50' } }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {unit.title}
                </Typography>
                {unit.unitNumber && (
                  <Typography variant="caption" color="text.secondary">
                    #{unit.unitNumber}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{unit.floorNumber != null ? `Floor ${unit.floorNumber}` : '—'}</TableCell>
              <TableCell><Typography variant="caption">{unit.propertyType}</Typography></TableCell>
              <TableCell>${unit.price?.toLocaleString() ?? '—'}</TableCell>
              <TableCell>
                {unit.unitStatus ? (
                  <Chip
                    label={unit.unitStatus.charAt(0) + unit.unitStatus.slice(1).toLowerCase()}
                    size="small"
                    color={unitStatusColor[unit.unitStatus] ?? 'default'}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">—</Typography>
                )}
              </TableCell>
              <TableCell>
                {unit.agent?.user
                  ? `${unit.agent.user.firstName} ${unit.agent.user.lastName}`
                  : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────
const ProjectDetailsDialog: React.FC<ProjectDetailsDialogProps> = ({
  open,
  onClose,
  project,
}) => {
  const [tab, setTab] = useState(0);

  if (!project) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PLANNING':           return 'info';
      case 'UNDER_CONSTRUCTION': return 'warning';
      case 'COMPLETED':          return 'success';
      case 'ON_HOLD':            return 'default';
      case 'CANCELLED':          return 'error';
      default:                   return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {project.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip
                label={project.status.replace(/_/g, ' ')}
                color={getStatusColor(project.status) as any}
                size="small"
              />
              <Chip
                label={project.constructionStatus === 'COMPLETED' ? 'Completed' : 'Under Construction'}
                color={project.constructionStatus === 'COMPLETED' ? 'success' : 'warning'}
                size="small"
              />
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Details" icon={<Construction fontSize="small" />} iconPosition="start" />
          <Tab label="Units" icon={<ListIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Floor Plan" icon={<Layers fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ minHeight: 400 }}>
        {/* ── Tab 0: Project Details ── */}
        <TabPanel value={tab} index={0}>
          <Grid container spacing={3}>
            {project.description && (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary">
                  {project.description}
                </Typography>
              </Grid>
            )}

            {/* Progress */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Project Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={project.progress}
                  sx={{ flexGrow: 1, height: 10, borderRadius: 1 }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {project.progress}%
                </Typography>
              </Box>
            </Grid>

            {/* Owner */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2, mb: 2 }}>
                👤 Owner Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Owner Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{project.ownerName}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Owner Phone</Typography>
                  <Typography variant="body1" fontWeight={600}>{project.ownerPhone}</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Location */}
            {(project.governorate || project.district || project.town || project.city || project.address) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2, mb: 2 }}>
                    📍 Location
                  </Typography>
                </Grid>
                {project.governorate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Governorate</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.governorate}</Typography>
                  </Grid>
                )}
                {project.district && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">District</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.district}</Typography>
                  </Grid>
                )}
                {project.town && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Town</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.town}</Typography>
                  </Grid>
                )}
                {project.city && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">City</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.city}</Typography>
                  </Grid>
                )}
                {project.address && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Address</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.address}</Typography>
                  </Grid>
                )}
                {project.googleMapsLink && (
                  <Grid item xs={12}>
                    <Button variant="outlined" startIcon={<OpenInNew />} href={project.googleMapsLink} target="_blank" size="small">
                      Open in Google Maps
                    </Button>
                  </Grid>
                )}
              </>
            )}

            {/* Coordinates */}
            {project.latitude && project.longitude && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  📍 Coordinates
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Latitude: <strong>{project.latitude.toFixed(6)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Longitude: <strong>{project.longitude.toFixed(6)}</strong>
                  </Typography>
                  {project.googleMapsLink && (
                    <Button size="small" variant="outlined" startIcon={<OpenInNew />} href={project.googleMapsLink} target="_blank" sx={{ ml: 'auto' }}>
                      View on Map
                    </Button>
                  )}
                </Box>
              </Grid>
            )}

            {/* Construction Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2, mb: 2 }}>
                🏗️ Construction Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Construction color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Construction Status</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {project.constructionStatus === 'COMPLETED' ? 'Completed' : 'Under Construction'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {project.constructionStatus === 'UNDER_CONSTRUCTION' && project.completionPercentage !== undefined && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Completion Percentage</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress variant="determinate" value={project.completionPercentage} sx={{ flexGrow: 1, height: 8, borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={600}>{project.completionPercentage}%</Typography>
                </Box>
              </Grid>
            )}
            {project.totalFloors && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Apartment color="primary" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Floors</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.totalFloors}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {project.totalApartments !== undefined && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MeetingRoom color="primary" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Apartments</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.totalApartments}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {project.availableApartments !== undefined && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MeetingRoom color="success" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Available Apartments</Typography>
                    <Typography variant="body1" fontWeight={600}>{project.availableApartments}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Timeline */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2, mb: 2 }}>
                📅 Timeline
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="primary" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {format(new Date(project.startDate), 'MMMM dd, yyyy')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {project.endDate && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday color="primary" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Expected End Date</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {format(new Date(project.endDate), 'MMMM dd, yyyy')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Created At</Typography>
              <Typography variant="body2">
                {format(new Date(project.createdAt), 'MMMM dd, yyyy HH:mm')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Last Updated</Typography>
              <Typography variant="body2">
                {format(new Date(project.updatedAt), 'MMMM dd, yyyy HH:mm')}
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        {/* ── Tab 1: Units ── */}
        <TabPanel value={tab} index={1}>
          <ProjectUnitsTab projectId={project.id} />
        </TabPanel>

        {/* ── Tab 2: Floor Plan ── */}
        <TabPanel value={tab} index={2}>
          <FloorPlanView projectId={project.id} />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDetailsDialog;
